import React, { useRef } from "react"
import { motion, useMotionValue } from "framer-motion"
import { PiEyeDuotone } from "react-icons/pi"
import { getDemoTourDaysLeft, isDemoTourOn } from "../services/demoTour/demoTourFlag"
import { exitDemoTour } from "../services/demoTour/demoTour.api"
import "./NewVersionPill.scss"
import "./DemoTourPill.scss"

// Where the pill was dragged to: this browser only, never sent anywhere
const POSITION_KEY = "mx-demo-tour-pill"

// The offset from its bottom right corner, kept on screen if the window shrank since
const readPosition = () => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(POSITION_KEY) || "null")
        const clamp = (value, size) => Math.min(0, Math.max(-size, Number(value) || 0))

        return { x: clamp(saved?.x, window.innerWidth), y: clamp(saved?.y, window.innerHeight) }
    } catch {
        return { x: 0, y: 0 }
    }
}

/**
 * Over every screen while this browser is on the demo tour (services/demoTour): says it
 * is read only and how long is left, and takes the person back to their own missionX.
 *
 * It can be dragged out of the way (kept on screen); where it was left is remembered in
 * this browser's local storage only.
 */
const DemoTourPill = () => {
    if (!isDemoTourOn()) return null

    return <DraggablePill />
}

const DraggablePill = () => {
    const boundsRef = useRef()
    const start = useRef(null)

    if (!start.current) start.current = readPosition()

    const x = useMotionValue(start.current.x)
    const y = useMotionValue(start.current.y)
    // A drag ends with a click: that one must not exit the tour
    const dragged = useRef(false)
    const days = getDemoTourDaysLeft()

    return (
        <>
            <div ref={boundsRef} className="x-new-version-bounds" />
            <motion.div
                className="x-new-version x-demo-tour"
                data-testid="demo-tour-pill"
                style={{ x, y }}
                drag
                dragConstraints={boundsRef}
                dragElastic={0.1}
                dragMomentum={false}
                onPointerDown={() => {
                    dragged.current = false
                }}
                onDragStart={() => {
                    dragged.current = true
                }}
                onDragEnd={() => {
                    try {
                        window.localStorage.setItem(POSITION_KEY, JSON.stringify({ x: x.get(), y: y.get() }))
                    } catch {
                        // No storage: it just isn't remembered
                    }
                }}
            >
                <div className="x-new-version-pill">
                    <PiEyeDuotone /> Read-only demo, {days} day{days === 1 ? "" : "s"} left
                    <span
                        className="x-demo-tour-exit"
                        data-testid="demo-tour-exit"
                        onClick={() => {
                            if (!dragged.current) exitDemoTour()
                        }}
                    >
                        Exit
                    </span>
                </div>
            </motion.div>
        </>
    )
}

export default DemoTourPill
